import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { shiftApi } from '@/services/api';
import { AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { formatMoney } from '@/utils/currency';
import VarianceApprovalModal from './VarianceApprovalModal';

export default function PendingShiftApprovals() {
  const { t } = useTranslation();
  const [selectedVariance, setSelectedVariance] = useState<any>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');

  const { data: variances = [], isLoading } = useQuery({
    queryKey: ['pendingVariances'],
    queryFn: async () => {
      const res = await shiftApi.listPendingVariances();
      return res.data.message || [];
    },
    refetchInterval: 60000, // Refresh every minute
  });

  if (isLoading) {
    return (
      <div className="card p-5 mb-6 animate-pulse">
        <div className="h-6 w-48 bg-slate-200 rounded mb-4"></div>
        <div className="h-20 bg-slate-100 rounded"></div>
      </div>
    );
  }

  if (!variances.length) {
    return null; // Hide if nothing is pending
  }

  const handleAction = (variance: any, type: 'approve' | 'reject') => {
    setSelectedVariance(variance);
    setActionType(type);
  };

  return (
    <>
      <div className="card p-0 mb-6 border-amber-200 shadow-sm overflow-hidden">
        <div className="bg-amber-50 px-5 py-4 border-b border-amber-100 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <h3 className="font-semibold text-amber-900">
            {t('dashboard.pendingShiftApprovals', { defaultValue: 'Pending Shift Approvals' })}
          </h3>
          <span className="bg-amber-100 text-amber-800 text-xs font-bold px-2 py-0.5 rounded-full ml-auto">
            {variances.length}
          </span>
        </div>
        
        <div className="divide-y divide-slate-100">
          {variances.map((variance: any) => (
            <div key={variance.name} className="p-5 flex flex-col lg:flex-row lg:items-center gap-4 hover:bg-slate-50 transition-colors">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-slate-900">{variance.cashier_name}</span>
                  <span className="text-xs text-slate-500 font-mono bg-slate-100 px-2 py-0.5 rounded">
                    {variance.pos_shift}
                  </span>
                </div>
                {variance.cashier_remarks && (
                  <p className="text-sm text-slate-600 italic mb-2">
                    "{variance.cashier_remarks}"
                  </p>
                )}
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm mt-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-500">Expected:</span>
                    <span className="font-medium">{formatMoney(variance.expected)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-500">Counted:</span>
                    <span className="font-medium">{formatMoney(variance.counted)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-500">Difference:</span>
                    <span className={`font-bold ${variance.difference < 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                      {formatMoney(variance.difference)}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2 shrink-0 border-t lg:border-t-0 pt-3 lg:pt-0">
                <button
                  onClick={() => handleAction(variance, 'approve')}
                  className="flex-1 lg:flex-none flex items-center justify-center gap-1.5 px-4 py-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 rounded-lg text-sm font-medium transition-colors"
                >
                  <CheckCircle className="w-4 h-4" />
                  Approve
                </button>
                <button
                  onClick={() => handleAction(variance, 'reject')}
                  className="flex-1 lg:flex-none flex items-center justify-center gap-1.5 px-4 py-2 bg-white text-slate-700 hover:bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium transition-colors"
                >
                  <XCircle className="w-4 h-4" />
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedVariance && (
        <VarianceApprovalModal
          variance={selectedVariance}
          actionType={actionType}
          onClose={() => setSelectedVariance(null)}
        />
      )}
    </>
  );
}
